import { useCallback, useEffect, useRef } from 'react'
import { useSetAtom } from 'jotai'
import { useNavigate } from '@tanstack/react-router'
import { AxiosError } from 'axios'
import { UserService } from '@duck/api-definitions'
import { invitationDataAtom } from '@/store/userAtoms'
import { useAuth } from '@/hooks/useAuth'
import { useTar } from '@/modules'
import { Button } from '@duck/ui'
import { apis } from '@/modules/apiClients'
import { toast } from 'sonner'

interface InviteGateAlreadyRegisteredProps {
  details: UserService.InvitationDetails
  token: string
}

export const InviteGateAlreadyRegistered = ({ details, token }: InviteGateAlreadyRegisteredProps) => {
  const { t } = useTar()
  const navigate = useNavigate()
  const setInvitationData = useSetAtom(invitationDataAtom)
  const { isAuthenticated, user, openLoginDialog } = useAuth()
  const claimedRef = useRef(false)

  // Mirror the invitation into the global atom so the LoginDialog renders
  // the "you're logging in via an invite" affordance.
  useEffect(() => {
    setInvitationData(details)
    return () => setInvitationData(null)
  }, [details, setInvitationData])

  // Once the user becomes authenticated, claim the invitation server-side and
  // bounce them to a meaningful destination so they don't stare at this view.
  useEffect(() => {
    if (!isAuthenticated || claimedRef.current) return
    claimedRef.current = true
    apis.userService
      .invitationTokenClaimPost(token)
      .catch(err => {
        if (err instanceof AxiosError && err.response?.status === 403) {
          toast.error(t('invite.wrongAccount.title'))
        }
        // Other failures: silent. The user is logged in, the invitation just
        // didn't get marked accepted. Worth a follow-up review (see Phase 5+).
      })
      .finally(() => {
        if (user?.role === UserService.UserRole.Partner) navigate({ to: '/partner/dashboard' })
        else if (user?.personId) navigate({ to: '/challenges/available' })
        else navigate({ to: '/challenges/available' })
      })
  }, [isAuthenticated, token, t, navigate, user])

  const handleLogin = useCallback(() => {
    openLoginDialog()
  }, [openLoginDialog])

  const body = details.role === UserService.UserRole.Admin
    ? t('invite.alreadyRegistered.adminBody', { email: details.email ?? '' })
    : details.role === UserService.UserRole.Partner
      ? t('invite.alreadyRegistered.partnerBody', { email: details.email ?? '', organizationName: details.organizationName ?? '' })
      : t('invite.alreadyRegistered.body', { email: details.email ?? '' })

  return (
    <div className="w-full max-w-md space-y-4 text-center">
      <h1 className="text-2xl font-semibold">{t('invite.alreadyRegistered.title')}</h1>
      <p className="text-sm text-muted-foreground">{body}</p>
      <Button onClick={handleLogin}>{t('invite.alreadyRegistered.loginCta')}</Button>
    </div>
  )
}
